import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function Flexbox_Intro() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📐 Flexbox Introduction</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Flexbox is a CSS layout module designed to help you easily align and
        distribute space among items in a container, even when their size is
        unknown or dynamic. It is perfect for creating responsive layouts.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #dbeafe, #bfdbfe)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#1e3a8a" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>display: flex</code> on a container to enable flexbox.
          </li>
          <li>
            <code>flex-direction</code> controls the main axis (row or column).
          </li>
          <li>
            <code>justify-content</code> aligns items along the main axis.
          </li>
          <li>
            <code>align-items</code> aligns items along the cross axis.
          </li>
          <li>
            <code>flex-wrap</code> allows items to wrap onto multiple lines.
          </li>
          <li>
            Flexbox is great for responsive layouts without float or positioning
            hacks.
          </li>
        </ul>
      </div>

      {/* Flexbox Example */}
      <h2 className="section-title">🎨 Simple Flexbox Example</h2>
      <CodeCopyBlock
        code={`.flex-container {
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  gap: 10px;
}
.flex-item {
  background: #3b82f6;
  color: white;
  padding: 20px;
  border-radius: 8px;
}`}
      />
      <div
        className="flex-container"
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-around",
          alignItems: "center",
          gap: "10px",
          marginBottom: "2rem",
        }}
      >
        <div
          className="flex-item"
          style={{
            background: "#3b82f6",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          Item 1
        </div>
        <div
          className="flex-item"
          style={{
            background: "#2563eb",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          Item 2
        </div>
        <div
          className="flex-item"
          style={{
            background: "#60a5fa",
            color: "white",
            padding: "20px",
            borderRadius: "8px",
          }}
        >
          Item 3
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Try <strong>flex-wrap</strong> to see how items wrap on smaller
            screens.
          </li>
          <li>
            Experiment with <strong>align-items</strong> and{" "}
            <strong>align-self</strong> for vertical alignment.
          </li>
          <li>
            Use <strong>justify-content: space-between</strong> and{" "}
            <strong>space-evenly</strong> for spacing control.
          </li>
          <li>
            Combine flexbox with <strong>media queries</strong> for fully
            responsive layouts.
          </li>
          <li>
            Explore <strong>nested flex containers</strong> for complex UI
            patterns.
          </li>
        </ul>
      </div>
    </div>
  );
}
